<!-- =========================================================================================
    File Name: Subscription.vue
    Description: Subscription page
========================================================================================== -->

<template>
  <div>
    <subscription-sidebar :isSidebarActive="dataSidebar" @closeSidebar="toggleDataSidebar" :data="sidebarData"/>
    <vx-card class="overflow-auto">
      <vs-button icon-pack="feather" icon="icon-download" @click="exportExcel()">{{$t('EXPORT_EXCEL')}}</vs-button>
      <v-server-table id="subscription-table" ref="subscriptionTable" name="subscriptionTable" 
        :columns="columns" :options="options" @loading="onLoading" @loaded="onLoaded" @sorted="setSortedForExcel" @filter="setFilterForExcelIncludeDateFilter">
        <vs-chip slot="inAppBillingStatus" slot-scope="props" :color="getInAppBillingStatusColor(props.row.subscription.inAppBillingStatus)" class="w-full" >
          {{$t(setInAppBillingStatus(props.row.subscription.inAppBillingStatus))}}</vs-chip>
        <div slot="usedStreamingTime" slot-scope="props">
          <span :class="getPercentClass(getPercentage(props.row.subscription.usedStreamingTime, props.row.subscription.streamingQuotaTime))"> 
            {{getPercentage(props.row.subscription.usedStreamingTime, props.row.subscription.streamingQuotaTime)}} % </span>
        </div>
        <div slot="uploadUsedTraffic" slot-scope="props">
          <span :class="getPercentClass(getPercentage(props.row.subscription.uploadUsedTraffic, props.row.subscription.uploadTrafficQuota))"> 
            {{getPercentage(props.row.subscription.uploadUsedTraffic, props.row.subscription.uploadTrafficQuota)}} % </span>
        </div>
        <div slot="downloadUsedTraffic" slot-scope="props">
          <span :class="getPercentClass(getPercentage(props.row.subscription.downloadUsedTraffic, props.row.subscription.downloadTrafficQuota))"> 
            {{getPercentage(props.row.subscription.downloadUsedTraffic, props.row.subscription.downloadTrafficQuota)}} % </span>
        </div>
        <div slot="detail" slot-scope="props">
          <feather-icon icon="EyeIcon" svgClasses="w-5 h-5 hover:text-primary stroke-current" @click.stop="seeDetail(props.row)" />
        </div>
      </v-server-table>
    </vx-card>
  </div>
</template>

<script>
let FileSaver = require('file-saver');

import SubscriptionSidebar from './SubscriptionSidebar.vue'
import moduleSubscription from "@/store/subscription/moduleSubscription.js"

export default {
  components: {
    SubscriptionSidebar
  },
  data() {
    return {
      // Data Sidebar
      dataSidebar: false,
      sidebarData: {},
      
      // Table
      columns: ['userId', 'inAppBillingStatus', 'lastBillingDate', 'expirationDate', 'terminationDate', 'usedStreamingTime', 'uploadUsedTraffic', 'downloadUsedTraffic', 'detail'],
      options: {
        perPage:10,
        skin: 'table',
        filterByColumn: true,
        filterable: ['userId', 'inAppBillingStatus', 'lastBillingDate',  'expirationDate', 'terminationDate' ],
        sortable: ['userId', 'inAppBillingStatus', 'lastBillingDate',  'expirationDate', 'terminationDate', 'usedStreamingTime', 'uploadUsedTraffic', 'downloadUsedTraffic',],
        orderBy: { ascending: true },
        debounce: 1000,
        dateColumns:['lastBillingDate', 'expirationDate', 'terminationDate'],
        dateFormat: 'YYYY-MM-DD',
        toMomentFormat: 'YYYYMMDD',
        datepickerOptions: {
            showDropdowns: true,
            autoUpdateInput: true,
        },
        sortIcon: {
          base: 'feather',
          up: 'icon-arrow-up',
          down: 'icon-arrow-down',
        },
        texts:{
          filterBy: this.$t("FILTER_BY"),
          defaultOption: this.$t("DEFAULT_OPTION"),
        },
        headings: {
          'userId': () => this.$t("USER_ID"),
          'inAppBillingStatus': () => this.$t("SUBSCRIPTION_YN"),
          'lastBillingDate': () => this.$t("LAST_BILLING_DATE"),
          'expirationDate': () => this.$t("EXPIRATION_DATE"),
          'terminationDate': () => this.$t("TERMINATION_DATE"),
          'usedStreamingTime': () => this.$t("USED_STREAMING_TIME"),
          'uploadUsedTraffic': () => this.$t("UPLOAD_USED_TRAFFIC"),
          'downloadUsedTraffic': () => this.$t("DOWNLOAD_USED_TRAFFIC"),
          'detail': () => this.$t("DETAIL"),
        },
        listColumns:{
          inAppBillingStatus:[
            {
              id: '1',
              text: this.$t("NORMAL")
            },
            {
              id: '2',
              text: this.$t("NO_PAYMENT")
            }
          ],
        },
        requestAdapter: function(data) {

          if(data.query.lastBillingDate){
            data.query = {...data.lastBillingDateAdditional, ...data.query}

            let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
            let start = moment.tz(data.query.lastBillingDate.start, localTimeZone).utc().format('YYYY-MM-DD HH:mm:ss')
            let end = moment.tz(data.query.lastBillingDate.end, localTimeZone).utc().format('YYYY-MM-DD HH:mm:ss')
            
            data.query.lastBillingDateUTC.start = start
            data.query.lastBillingDateUTC.end = end
          }

          if(data.query.expirationDate){
            data.query = {...data.expirationDateAdditional, ...data.query}

            let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
            let start = moment.tz(data.query.expirationDate.start, localTimeZone).utc().format('YYYY-MM-DD HH:mm:ss')
            let end = moment.tz(data.query.expirationDate.end, localTimeZone).utc().format('YYYY-MM-DD HH:mm:ss')
            
            data.query.expirationDateUTC.start = start
            data.query.expirationDateUTC.end = end
          }

          if(data.query.terminationDate){
            data.query = {...data.terminationDateAdditional, ...data.query}

            let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
            let start = moment.tz(data.query.terminationDate.start, localTimeZone).utc().format('YYYY-MM-DD HH:mm:ss')
            let end = moment.tz(data.query.terminationDate.end, localTimeZone).utc().format('YYYY-MM-DD HH:mm:ss')
            
            data.query.terminationDateUTC.start = start
            data.query.terminationDateUTC.end = end
          }

          return {
            ascending: data.ascending,
            byColumn: data.byColumn,
            pageSize: data.limit,
            orderBy: data.orderBy ? data.orderBy : "userId",
            page: data.page,
            query: data.query,
            pageResult: ((data.page-1)*data.limit)
          };
        },
        params: {
          lastBillingDateAdditional:{
            lastBillingDateUTC: {
              start: '',
              end: ''
            },
          },
          expirationDateAdditional:{
            expirationDateUTC: {
              start: '',
              end: ''
            }
          },
          terminationDateAdditional:{
            terminationDateUTC: {
              start: '',
              end: ''
            }
          } 
        },
        requestFunction: function (data) {
          return this.$http.get('/api/subscription-management/subscriptions', {
                params: data
          }).catch(function (e) {
                this.dispatch('error', e);
                this.$vs.loading.close()
          }.bind(this));
        },
        responseAdapter: (response) => {
          this.$store.commit('dataSubscription/SET_SUBSCRIPTION',response.data)
          return {
            data: this.$store.state.dataSubscription.subscriptionList,
            count: this.$store.state.dataSubscription.subscriptionCount
          }
        },
        templates:{
          userId: function(h,row){
            return row.user.userId;
          },
          inAppBillingStatus: function(h,row){
            return row.subscription.inAppBillingStatus;
          },
          lastBillingDate: function(h,row){
            let lastBillingDate = row.subscription.lastBillingDate
            if(!lastBillingDate){
              return "-"
            }else {
              let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
              let dateTime = this.$moment(lastBillingDate,'YYYYMMDDHHmmss').format()
              let dateTimeToLocal = this.$moment.tz(dateTime, localTimeZone).format('YYYY-MM-DD')
              
              return dateTimeToLocal
            }

          },
          expirationDate: function(h,row){
            let expirationDate = row.subscription.expirationDate
            if(!expirationDate){
              return "-"
            }else {
              let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
              let dateTime = this.$moment(expirationDate,'YYYYMMDDHHmmss').format()
              let dateTimeToLocal = this.$moment.tz(dateTime, localTimeZone).format('YYYY-MM-DD')
              
              return dateTimeToLocal
            }
          },
          terminationDate: function(h,row){
            let terminationDate = row.subscription.terminationDate
            if(!terminationDate){
              return "-"
            }else {
              let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
              let dateTime = this.$moment(terminationDate,'YYYYMMDDHHmmss').format()
              let dateTimeToLocal = this.$moment.tz(dateTime, localTimeZone).format('YYYY-MM-DD')
              
              return dateTimeToLocal
            } 
          }
        }
      }
    }
  },
  created() {
    if (!moduleSubscription.isRegistered) {
      this.$store.registerModule('dataSubscription', moduleSubscription)
      moduleSubscription.isRegistered = true
    }
  },
  methods: {
    // Sidebar
    toggleDataSidebar(val=false) {
      this.dataSidebar = val
    },
    seeDetail(data) {
      this.sidebarData = data
      this.toggleDataSidebar(true)
    },
    getInAppBillingStatusColor(status){
      return this.$store.state.commonConfig.inAppBillingStatus.properties[status].color
    },
    setInAppBillingStatus(status) {
      return this.$store.state.commonConfig.inAppBillingStatus.properties[status].name
    },
    getPercentage(used, quota){
      if (used === 0) return 0
      if (quota === 0) return 0
      else return Math.round((used/quota)*100)
    },
    getPercentClass(percent){
      if (percent >= 50 && percent < 90) {
        return "text-primary font-bold"
      }else if(percent >= 90){
        return "text-danger font-bold"
      }
    },
    exportExcel(){

      let locale = this.$i18n.locale

      this.$http({
        method: 'post',
        url: '/api/subscription-management/subscriptions/excel/'+locale,
        data:{
          query : JSON.stringify(this.filterForExcel),
          orderBy:  this.sortForExcel.column ? this.sortForExcel.column : 'userId' ,
          ascending: this.sortForExcel.ascending ? 1 : 0,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        withCredentials: true,
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }).then((response) => {
        let excelTitle
        if (locale === "ko") {
          excelTitle = "구독_목록.xlsx"
        } else {
          excelTitle ="Subscription_List.xlsx"
        }
        FileSaver.saveAs(response.data, excelTitle)
      }).catch((error)=> {
        console.log(error);
      }) 
    }
  },
}

</script>

<style lang="scss" scoped>
#subscription-table {
  text-align: center;
}
</style>